import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sensorsApi, zonesApi, parcelsApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Radio,
  Plus,
  MoreHorizontal,
  Pencil,
  Trash2,
  Loader2,
  Wifi,
  WifiOff,
  Wrench,
  Battery,
  Thermometer,
  Droplets,
  Wind,
  Gauge,
  Sun,
  Activity,
} from 'lucide-react';

const SensorsPage = () => {
  const navigate = useNavigate();
  const [sensors, setSensors] = useState([]);
  const [zones, setZones] = useState([]);
  const [parcels, setParcels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSensor, setEditingSensor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    sensor_type: 'soil_moisture',
    model: '',
    serial_number: '',
    zone_id: '',
    parcel_id: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const { isAdmin } = useAuth();
  const { socket } = useSocket();

  const fetchData = async () => {
    try {
      const [sensorsRes, zonesRes, parcelsRes] = await Promise.all([
        sensorsApi.getAll(),
        zonesApi.getAll(),
        parcelsApi.getAll(),
      ]);
      setSensors(sensorsRes.data);
      setZones(zonesRes.data);
      setParcels(parcelsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Real-time sensor updates
  useEffect(() => {
    if (!socket) return;

    socket.on('sensor_update', (data) => {
      setSensors((prev) =>
        prev.map((s) => (s.id === data.id ? { ...s, ...data } : s))
      );
    });

    return () => {
      socket.off('sensor_update');
    };
  }, [socket]);

  const handleOpenDialog = (sensor = null) => {
    if (sensor) {
      setEditingSensor(sensor);
      setFormData({
        name: sensor.name,
        sensor_type: sensor.sensor_type,
        model: sensor.model || '',
        serial_number: sensor.serial_number || '',
        zone_id: sensor.zone_id || '',
        parcel_id: sensor.parcel_id || '',
      });
    } else {
      setEditingSensor(null);
      setFormData({
        name: '',
        sensor_type: 'soil_moisture',
        model: '',
        serial_number: '',
        zone_id: zones[0]?.id || '',
        parcel_id: '',
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const data = {
      name: formData.name,
      sensor_type: formData.sensor_type,
      model: formData.model || null,
      serial_number: formData.serial_number || null,
      zone_id: formData.zone_id || null,
      parcel_id: formData.parcel_id || null,
    };

    try {
      if (editingSensor) {
        await sensorsApi.update(editingSensor.id, {
          name: data.name,
          sensor_type: data.sensor_type,
          model: data.model,
          serial_number: data.serial_number,
        });
        toast.success('Capteur mis à jour');
      } else {
        await sensorsApi.create(data);
        toast.success('Capteur créé');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce capteur ?')) return;

    try {
      await sensorsApi.delete(id);
      toast.success('Capteur supprimé');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const getZoneName = (zoneId) => {
    const zone = zones.find((z) => z.id === zoneId);
    return zone?.name || '-';
  };

  const statusConfig = {
    online: { label: 'En ligne', icon: Wifi, color: 'bg-emerald-500 text-white' },
    offline: { label: 'Hors ligne', icon: WifiOff, color: 'bg-red-500 text-white' },
    maintenance: { label: 'Maintenance', icon: Wrench, color: 'bg-amber-500 text-white' },
  };

  const sensorTypeConfig = {
    soil_moisture: { label: 'Humidité sol', icon: Droplets },
    soil_temperature: { label: 'Température sol', icon: Thermometer },
    air_temperature: { label: 'Température air', icon: Thermometer },
    humidity: { label: 'Humidité air', icon: Droplets },
    weather_station: { label: 'Station météo', icon: Wind },
    soil_ph: { label: 'pH sol', icon: Gauge },
    flow_meter: { label: 'Débitmètre', icon: Activity },
    light: { label: 'Luminosité', icon: Sun },
  };

  const formatReading = (sensor) => {
    if (!sensor.last_reading) return '-';
    const type = sensor.sensor_type;
    const value = sensor.last_reading;

    switch (type) {
      case 'soil_moisture':
      case 'humidity':
        return `${value.toFixed(1)}%`;
      case 'soil_temperature':
      case 'air_temperature':
        return `${value.toFixed(1)}°C`;
      case 'soil_ph':
        return value.toFixed(1);
      case 'flow_meter':
        return `${value.toFixed(1)} L/min`;
      case 'light':
        return `${value.toFixed(0)} lux`;
      default:
        return value.toString();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sensors-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Capteurs</h1>
          <p className="text-muted-foreground mt-1">Surveillez vos capteurs en temps réel</p>
        </div>
        {isAdmin && zones.length > 0 && (
          <Button onClick={() => handleOpenDialog()} data-testid="add-sensor-btn">
            <Plus className="mr-2 h-4 w-4" />
            Nouveau capteur
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950/30">
                <Wifi className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{sensors.filter((s) => s.status === 'online').length}</p>
                <p className="text-sm text-muted-foreground">En ligne</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-950/30">
                <WifiOff className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{sensors.filter((s) => s.status === 'offline').length}</p>
                <p className="text-sm text-muted-foreground">Hors ligne</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-950/30">
                <Wrench className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{sensors.filter((s) => s.status === 'maintenance').length}</p>
                <p className="text-sm text-muted-foreground">En maintenance</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Radio className="h-5 w-5 text-primary" />
            Liste des capteurs
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sensors.length === 0 ? (
            <div className="text-center py-12">
              <Radio className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Aucun capteur</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Capteur</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Zone</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Dernière lecture</TableHead>
                  <TableHead>Batterie</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sensors.map((sensor) => {
                  const status = statusConfig[sensor.status] || statusConfig.offline;
                  const StatusIcon = status.icon;
                  const typeConfig = sensorTypeConfig[sensor.sensor_type] || { label: sensor.sensor_type, icon: Radio };
                  const TypeIcon = typeConfig.icon;

                  return (
                    <TableRow key={sensor.id} data-testid={`sensor-row-${sensor.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
                            <TypeIcon className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="font-medium">{sensor.name}</p>
                            {sensor.serial_number && (
                              <p className="text-xs text-muted-foreground">{sensor.serial_number}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {typeConfig.label}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {getZoneName(sensor.zone_id)}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${status.color} flex items-center gap-1 w-fit`}>
                          <StatusIcon className="h-3 w-3" />
                          {status.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-sm">{formatReading(sensor)}</span>
                      </TableCell>
                      <TableCell>
                        {sensor.battery_level !== null && sensor.battery_level !== undefined ? (
                          <div className="flex items-center gap-2">
                            <Battery
                              className={`h-4 w-4 ${
                                sensor.battery_level > 50
                                  ? 'text-emerald-500'
                                  : sensor.battery_level > 20
                                  ? 'text-amber-500'
                                  : 'text-red-500'
                              }`}
                            />
                            <span className="text-sm">{sensor.battery_level}%</span>
                          </div>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {isAdmin && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`sensor-menu-${sensor.id}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/sensors/${sensor.id}`)}>
                                <Activity className="mr-2 h-4 w-4" />
                                Voir détails
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleOpenDialog(sensor)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Modifier
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => handleDelete(sensor.id)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingSensor ? 'Modifier le capteur' : 'Nouveau capteur'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nom *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="sensor-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sensor_type">Type de capteur *</Label>
                <Select
                  value={formData.sensor_type}
                  onValueChange={(value) => setFormData({ ...formData, sensor_type: value })}
                >
                  <SelectTrigger data-testid="sensor-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(sensorTypeConfig).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        {config.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {!editingSensor && (
                <div className="space-y-2">
                  <Label htmlFor="zone_id">Zone</Label>
                  <Select
                    value={formData.zone_id}
                    onValueChange={(value) => setFormData({ ...formData, zone_id: value })}
                  >
                    <SelectTrigger data-testid="sensor-zone-select">
                      <SelectValue placeholder="Sélectionner une zone" />
                    </SelectTrigger>
                    <SelectContent>
                      {zones.map((zone) => (
                        <SelectItem key={zone.id} value={zone.id}>
                          {zone.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="model">Modèle</Label>
                  <Input
                    id="model"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    data-testid="sensor-model-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="serial_number">Numéro de série</Label>
                  <Input
                    id="serial_number"
                    value={formData.serial_number}
                    onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                    data-testid="sensor-serial-input"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={submitting} data-testid="sensor-submit-btn">
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editingSensor ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SensorsPage;
